<template>
    <v-text-field
        v-bind="$attrs"
        :value="text"
        :suffix="suffixText"
        :disabled="disabled"
        :label="label"
        :error-messages="errorMessages"
        type="text"
        inputmode="decimal"
        autocomplete="off"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="$emit('keydown', $event)"
    />
</template>

<script>
import {
    formatDecimalForInput,
    getCurrencySymbol,
    parseDecimalInput,
    DEFAULT_CURRENCY,
} from "@/utils/numberFormat";

/**
 * Locale-aware monetary amount input.
 * Accepts both comma and point as decimal separator, displays the app locale
 * separator, and emits a normalized Number (or null when empty/invalid).
 */
export default {
    name: "AmountInput",
    inheritAttrs: false,
    props: {
        value: {
            type: [Number, String],
            default: null,
        },
        currency: {
            type: String,
            default: DEFAULT_CURRENCY,
        },
        // Set to null/empty string to hide the currency suffix.
        suffix: {
            type: String,
            default: undefined,
        },
        maxDecimals: {
            type: Number,
            default: 2,
        },
        allowNegative: {
            type: Boolean,
            default: false,
        },
        label: {
            type: String,
            default: "",
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        errorMessages: {
            type: [Array, String],
            default: () => [],
        },
    },
    data() {
        return {
            text: "",
            focused: false,
        };
    },
    computed: {
        suffixText() {
            if (this.suffix !== undefined) {
                return this.suffix || undefined;
            }
            return getCurrencySymbol(this.currency);
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                if (!this.focused) {
                    this.text = formatDecimalForInput(
                        newValue,
                        this.maxDecimals
                    );
                }
            },
        },
    },
    methods: {
        onInput(raw) {
            this.text = raw;
            this.$emit("input", this.parse(raw));
        },
        onFocus(event) {
            this.focused = true;
            this.$emit("focus", event);
        },
        onBlur(event) {
            this.focused = false;
            this.text = formatDecimalForInput(
                this.parse(this.text),
                this.maxDecimals
            );
            this.$emit("blur", event);
        },
        parse(raw) {
            return parseDecimalInput(raw, {
                maxDecimals: this.maxDecimals,
                allowNegative: this.allowNegative,
            });
        },
    },
};
</script>
